
import { useState, useEffect, useRef } from 'react';

export default function ApproachSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const principles = [
    {
      title: 'Strategic Foundation',
      description: 'Every design decision is backed by deep market research and clear brand objectives.',
      icon: 'ri-compass-3-line'
    },
    {
      title: 'Emotional Resonance',
      description: 'Creating identities that connect with your audience on a personal and memorable level.',
      icon: 'ri-heart-pulse-line'
    },
    {
      title: 'Visual Harmony',
      description: 'Ensuring consistency and aesthetic excellence across all brand touchpoints.',
      icon: 'ri-palette-line'
    },
    {
      title: 'Future-Ready Adaptability',
      description: 'Building identities that are flexible and grow with your evolving business.',
      icon: 'ri-rocket-line'
    }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-purple-900/50 to-purple-800/50 relative"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `url('/assets/approach-bg.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Our Approach: Building Brands with 
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Soul & Strategy</span>
          </h2>
          <h3 className="text-2xl text-purple-200 mb-8">
            More Than Aesthetics: Strategic Branding from the Ground Up.
          </h3>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            We believe effective branding goes far beyond a beautiful logo. Our process is a collaborative 
            journey, combining insightful strategy with unparalleled creative execution. We work hand-in-hand 
            with you to understand your mission, audience, and market, ensuring every visual and message we 
            craft is rooted in purpose.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {principles.map((principle, index) => (
            <div
              key={index}
              className={`group bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-purple-400/20 hover:border-purple-400/50 transition-all duration-500 hover:transform hover:scale-105 cursor-pointer ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-6 group-hover:animate-pulse">
                <i className={`${principle.icon} text-2xl text-white`}></i>
              </div>
              
              <h4 className="text-xl font-bold text-white mb-4 group-hover:text-purple-300 transition-colors duration-300">
                {principle.title}
              </h4>
              
              <p className="text-gray-300 group-hover:text-white transition-colors duration-300 leading-relaxed">
                {principle.description}
              </p>
            </div>
          ))}
        </div>

        {/* Process Flow */}
        <div className={`mt-20 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-purple-400/20">
            <h4 className="text-2xl font-bold text-white text-center mb-8">Our Brand Building Process</h4>
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {['Discovery', 'Strategy', 'Design', 'Implementation', 'Evolution'].map((step, index) => (
                <div key={index} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {index + 1}
                    </div>
                    <span className="text-purple-200 font-medium mt-2">{step}</span>
                  </div>
                  {index < 4 && (
                    <div className="hidden md:block w-16 h-0.5 bg-gradient-to-r from-purple-400 to-pink-400 mx-4"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
